import { Component, Inject, OnInit, ViewChild, AfterViewInit, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { firstValueFrom, map } from 'rxjs';
import { SiteSequencerService } from 'src/app/components/site-sequencer/services/site-sequencer.service';
import { CommonService } from 'src/app/services/common.service';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

@Component({
  selector: 'app-modal-list-dialog',
  templateUrl: './modal-list-dialog.component.html',
  styleUrls: ['./modal-list-dialog.component.scss'],
  standalone: false
})
export class ModalListDialogComponent implements OnInit, AfterViewInit {
  displayedColumns = ['Manufacturer', 'Model', 'Description', 'Point Name'];
  dialogTitle!: string;
  dynamicPlaceholder: string = '';
  dataSource:any[] = [];
  @ViewChild('searchTextField') searchField!: ElementRef;
  originalData: any[] = [];
  searchText: string = '';
  sortActive: string = '';
  sortDirection: 'asc' | 'desc' | '' = 'asc';
  isLoading: boolean = false;
  selectedData!: any;

  selectedRowId: string | null = null;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { isPointModal?: boolean, displayData?: any[], selectedData: any, modalPoints?: any[] },
    public dialogRef: MatDialogRef<ModalListDialogComponent>,
    private siteSequencerService: SiteSequencerService, 
    private commonService: CommonService
  ) { }

  async ngOnInit() {
    if(this.data.isPointModal) {
      this.dialogTitle = 'Select Point';
      this.displayedColumns = ['Point Name'];
      this.dynamicPlaceholder = 'Search for Point';
    } else {
      this.dialogTitle = 'Select Model';
      this.dynamicPlaceholder = 'Search for Model';
      this.displayedColumns = ['Manufacturer', 'Model', 'Description'];
      this.data.displayData = await this.getmodelsList();
    }
    this.selectedRowId = this.data?.selectedData?.selectedId;
    this.originalData = this.data?.displayData || [];
    this.dataSource = [...this.originalData];
    setTimeout(() => this.scrollToSelectedRow(), 0);
  }

  ngAfterViewInit(): void {
    this.searchField.nativeElement.blur();
  }

  /**
   * Method to fetch the list of models from the service.
   * @returns {Promise<any[]>} - A promise that resolves to an array of models.
   */
  async getmodelsList(): Promise<any[]> {
    this.isLoading = true;
    try {
      const models = await firstValueFrom(
        this.siteSequencerService.getModalOptions().pipe(
          map((res: any) =>
            res.map((row: any) => {
              const version = row?.version ? `${row.version.major}.${row.version.minor}.${row.version.patch}` : '';
              this.isLoading = false;
              return {
                label: row?.name,
                value: row.id,
                manufacturerName: row?.manufacturerName ?? '',
                description: row?.description ?? '',
                version: version || null,
                versionLabel: version ? ` (v ${version})` : '',
              }
            })
          )
        )
      );
      return models;
    } catch (err) {
      console.error('Failed to fetch modal options:', err);
      this.commonService.showToast('Error', 'Failed to fetch external modals');
      this.isLoading = false;
      return []; // return empty array on failure
    }

  }

  onSortChange(event: { active: string; direction: 'asc' | 'desc' | '' }) {
    this.sortActive = event.active;
    this.sortDirection = event.direction;
  
    if (!event.direction) {
      // Reset to original order
      this.dataSource = [...this.originalData];
      return;
    }
  
    const sortedData = [...this.dataSource].sort((a, b) => {
      const aVal = (a[event.active] || '').toString().toLowerCase();
      const bVal = (b[event.active] || '').toString().toLowerCase();
      return aVal.localeCompare(bVal) * (event.direction === 'asc' ? 1 : -1);
    });
  
    this.dataSource = sortedData;
  }

  applyFilter(filterValue: any): void {
    const searchTerm = (filterValue || '').toLowerCase().trim();
  
    this.dataSource = (this.originalData || []).filter(item => {
      const label = item?.label?.toLowerCase() || '';
      const manufacturer = item?.manufacturerName?.toLowerCase() || '';
      const description = item?.description?.toLowerCase() || '';
  
      return label.includes(searchTerm) || manufacturer.includes(searchTerm) || description.includes(searchTerm);
    });
  }
  
  clearSearch() {
    this.searchText = '';
    this.applyFilter('');
  }

  selectRow(row: any): void {
    this.selectedRowId = row.value;
    const result = this.data.isPointModal ? row : {selectedModal: row, allModels: this.originalData};
    this.dialogRef.close(result);
  }

  trackByFn(index: number, item: any): any {
    return item.value ?? item.label;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  getViewportHeight() {
    const rowCount = this.dataSource.length;
    const maxVisibleRows = 14;
    return Math.min(rowCount, maxVisibleRows) * 26;
  }

  /**
   * Method to scroll to the selected row in the virtual scroll viewport.
   */
  scrollToSelectedRow(): void {
    if (!this.selectedRowId || !this.viewport) return;

    const index = this.dataSource.findIndex((item: any) => {
      const idMatch = item.value === this.selectedRowId;
      if (this.data.isPointModal) {
        return idMatch;
      }

      const versionMatch = item.version === this.data?.selectedData?.version;
      return idMatch && versionMatch;
    });

    if (index >= 0) {
      this.viewport.scrollToIndex(index, 'smooth');
    }
  }

}
